import { parseLogSearch, type LogFilter } from './logSearch';

/**
 * The audit report's filter, as it lives in the URL.
 *
 * THE SHARED PARSE MOVED TO `logSearch.ts` when the event log report arrived and needed the
 * identical one. What is left here is the audit report's own NAME for it, kept because a filter is
 * read in the vocabulary of the report it belongs to — and because `validateSearch` in the route,
 * the tests, and every component below it already say `AuditFilter`.
 *
 * AND WHAT IS LEFT IS ALSO THIS REPORT'S OWN THREE FIELDS. The audit log records what somebody
 * DID TO A RECORD — `subject_type` and `subject_id` are on every row of it — so "everything that
 * has ever happened to this house" is a question only this log can answer. The event log's
 * subjects come from a registry rather than from call sites, so it will want its own version of
 * these rather than a share of these; putting them in `LogFilter` would have put `?subjectType=`
 * on a report whose server input has no such field, where it would be silently dropped by zod and
 * then PRINTED in the heading as a narrowing that never happened.
 *
 * STILL AN EAGER MODULE. `validateSearch` runs during routing, so this file and everything it
 * imports is in the initial bundle; `logSearch.ts` is careful about that on both reports' behalf.
 * Anything the audit report needs but routing does not belongs in `auditFilter.ts` — which is why
 * the record type is shape-checked here and only turned into English there.
 */
export interface AuditFilter extends LogFilter {
  /** What kind of record — `property`, `owner`, `tenancy`. Absent means every kind. */
  subjectType?: string;
  /** One record of that type. Never set without `subjectType`; see the parse below. */
  subjectId?: string;
  /** A word or two to look for, across the action, its payload and the record it names. */
  q?: string;
}

/**
 * Read the filter out of the URL.
 *
 * SHAPE-CHECKED ONLY, deliberately — the same call `/financial/transactions` makes. A malformed
 * range is refused by the contract, which is the boundary the pasted URL crosses anyway, and the
 * server's sentence beside the filter is a better answer than a router error page.
 *
 * WITH ONE EXCEPTION, AND IT IS NOT SHAPE. A record id with no record TYPE beside it is refused by
 * the contract — the pair is what the subject index is built on — so leaving one in the filter
 * would produce a report that errors rather than one that is merely odd. A URL that has lost half
 * of the pair has lost the question; dropping the half that cannot be asked leaves an operator
 * with their report.
 */
export function parseAuditSearch(search: Record<string, unknown>): AuditFilter {
  const shared = parseLogSearch(search);

  // Lowercase identifiers, which is what every `writeAudit` call site writes and what the contract
  // accepts. Anything else is a hand-edited URL and would narrow to nothing.
  const subjectType =
    typeof search.subjectType === 'string' && /^[a-z][a-z0-9_]*$/.test(search.subjectType)
      ? search.subjectType
      : undefined;

  const subjectId =
    subjectType !== undefined && typeof search.subjectId === 'string' && search.subjectId !== ''
      ? search.subjectId
      : undefined;

  // Trimmed here rather than at the boundary: `?q=%20` is a filter that looks set, prints in the
  // heading, and narrows nothing.
  const q = typeof search.q === 'string' && search.q.trim() !== '' ? search.q.trim() : undefined;

  return {
    ...shared,
    ...(subjectType === undefined ? {} : { subjectType }),
    ...(subjectId === undefined ? {} : { subjectId }),
    ...(q === undefined ? {} : { q }),
  };
}
